import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Play, X, ChevronRight, ChevronLeft, Video, AlertCircle, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TutorialsModal = ({ isOpen, onClose }) => {
  const [tutorials, setTutorials] = useState([]);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTutorials();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTutorial) {
      setVideoLoading(true);
      setVideoError(null);
    }
  }, [selectedTutorial]);

  const fetchTutorials = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tutorials`);
      setTutorials(res.data.tutorials || []);
    } catch (err) {
      console.error("Failed to fetch tutorials:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownload = (tutorial) => {
    window.open(`${BACKEND_URL}${tutorial.file}`, '_blank');
  };

  const getVideoUrl = (tutorial) => {
    return `${BACKEND_URL}${tutorial.file}`;
  };

  const getPlayerUrl = (tutorial) => {
    // Use iframe player for better compatibility
    return `${BACKEND_URL}/api/tutorials/player/${tutorial.id}.html`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div 
        className="w-full max-w-5xl bg-slate-900 rounded-lg border-2 border-slate-700 shadow-xl animate-fade-in max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-slate-700 bg-slate-800">
          <div className="flex items-center gap-3">
            {selectedTutorial && (
              <button 
                onClick={() => {
                  setSelectedTutorial(null);
                  setVideoError(null);
                }} 
                className="p-2 hover:bg-slate-700 rounded-md transition-colors text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <Video className="w-6 h-6 text-orange-500" />
            <h2 className="text-xl font-bold text-white">
              {selectedTutorial ? selectedTutorial.title : "ShadowOps Tutorials"}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-slate-600 border-t-orange-500 rounded-full animate-spin" />
            </div>
          ) : selectedTutorial ? (
            /* Video Player View */
            <div className="space-y-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                {videoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                    <div className="w-8 h-8 border-4 border-slate-600 border-t-orange-500 rounded-full animate-spin" />
                  </div>
                )}
                {videoError ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                    <p className="text-lg">Unable to load video</p>
                    <p className="text-sm text-slate-400 mt-2">{videoError}</p>
                    <button 
                      onClick={() => handleDownload(selectedTutorial)}
                      className="mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-md"
                    >
                      Download Instead
                    </button>
                  </div>
                ) : (
                  <iframe
                    src={getPlayerUrl(selectedTutorial)}
                    className="w-full h-full border-0"
                    allow="autoplay; fullscreen"
                    data-testid="tutorial-video-player"
                    onLoad={() => setVideoLoading(false)}
                    onError={() => {
                      setVideoLoading(false);
                      setVideoError('Video playback failed. Try downloading instead.');
                    }}
                  />
                )}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{selectedTutorial.description}</p>
                  <p className="text-slate-500 text-xs mt-1">Duration: {selectedTutorial.duration || formatDuration(selectedTutorial.duration_seconds || 0)}</p>
                </div>
                <button 
                  onClick={() => handleDownload(selectedTutorial)}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md transition-colors"
                  data-testid="download-tutorial-btn"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          ) : (
            /* Tutorial List View */
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <p className="text-slate-400 text-sm mb-4">
                Learn how to use ShadowOps with these video guides narrated by our friendly Aussie guide.
              </p>
              {tutorials.map((tutorial, index) => {
                const pending = tutorial.pending_recording || tutorial.needs_recording;
                return (
                <button
                  key={tutorial.id}
                  onClick={() => { if (!pending) setSelectedTutorial(tutorial); }}
                  disabled={pending}
                  className={`w-full flex items-center gap-4 p-4 rounded-lg border text-left group ${
                    pending
                      ? 'bg-slate-800/50 border-slate-700 cursor-not-allowed opacity-70'
                      : 'bg-slate-800 hover:bg-slate-700 border-slate-700 transition-colors'
                  }`}
                  data-testid={`tutorial-item-${tutorial.id}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    pending ? 'bg-amber-500/20' : 'bg-orange-500/20 group-hover:bg-orange-500/30'
                  }`}>
                    <Play className={`w-5 h-5 ${pending ? 'text-amber-500' : 'text-orange-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-orange-500 text-sm font-mono">{String(index + 1).padStart(2, '0')}</span>
                      <h3 className="font-semibold text-white truncate">{tutorial.title}</h3>
                      {pending && (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm truncate">{tutorial.description}</p>
                  </div>
                  <div className="text-slate-500 text-sm flex-shrink-0">
                    {tutorial.duration || formatDuration(tutorial.duration_seconds || 0)}
                  </div>
                  <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-colors ${
                    pending ? 'text-slate-600' : 'text-slate-500 group-hover:text-orange-500'
                  }`} />
                </button>
                );
              })}
              
              {/* Total duration info */}
              <div className="pt-4 border-t border-slate-700 text-center">
                <p className="text-slate-500 text-sm">
                  {tutorials.length} tutorials • Total: ~{Math.round(tutorials.reduce((acc, t) => acc + (t.duration_seconds || 0), 0) / 60)} minutes
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default TutorialsModal;
